import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import { HeroReveal, ScrollReveal, TextReveal } from '@/components/motion'
import { CapabilityGrid, RotatedProcessCards, StickyFeatures, UiFragment } from '@/components/landing/service'
import { OUT_EASE } from '@/components/landing/service/halftone'
import { Stage, WindowBar, fragHair, fragMuted, useSeen } from './fragments'
import { PlatformCtaBand, PlatformFaq, TrialButton, hairline, ink, muted, type LayoutProps } from './shared'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = Array.from({ length: 24 }, (_, h) => h)

/** Sample weekly pattern: office hours on weekdays, a quiet weekend. */
function sampleLevel(day: number, hour: number): number {
  const weekday = day < 5
  const office = hour >= 8 && hour <= 18
  const peak = hour === 10 || hour === 11 || hour === 14 || hour === 15
  // Deterministic jitter so the grid reads as real data, not a gradient.
  const jitter = ((day * 31 + hour * 17) % 7) / 7
  if (!weekday) return office ? (jitter > 0.6 ? 1 : 0) : 0
  if (!office) return hour >= 6 && hour <= 21 && jitter > 0.55 ? 1 : 0
  if (peak) return jitter > 0.3 ? 4 : 3
  if (hour === 12 || hour === 13) return 2
  return jitter > 0.5 ? 3 : 2
}

const LEVELS = [
  'bg-[var(--color-landing-gray-100)] dark:bg-white/[0.06]',
  'bg-[#c9c6ff] dark:bg-[#2a2470]',
  'bg-[var(--color-landing-accent)]',
  'bg-[var(--color-landing-secondary)]',
  'bg-[#ff00f2]',
]

/** Hour-by-day usage heatmap, the hero picture (sample data, decorative). */
function Heatmap() {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.3 })
  const reduce = useReducedMotion()
  return (
    <div ref={ref} aria-hidden data-ui className="bg-white p-4 shadow-[0_40px_80px_-40px_rgba(10,10,10,0.3)] dark:bg-[#1c1c26] sm:p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={cn('font-semibold', ink)}>Documents generated · by hour and day</span>
        <span className={cn('flex items-center gap-2 text-sm', muted)}>
          Fewer
          {LEVELS.map((l) => (
            <span key={l} className={cn('h-3 w-3', l)} />
          ))}
          More
        </span>
      </div>
      <div className="mt-6 overflow-x-auto">
        <div className="grid min-w-[40rem] grid-cols-[2.5rem_repeat(24,minmax(0,1fr))] gap-[3px]">
          <span />
          {HOURS.map((h) => (
            <span key={h} className={cn('pb-1 text-center text-[11px] tabular-nums', muted)}>
              {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
            </span>
          ))}
          {DAYS.map((day, d) => (
            <div key={day} className="contents">
              <span className={cn('self-center text-xs', muted)}>{day}</span>
              {HOURS.map((h) => (
                <motion.span
                  key={h}
                  className={cn('aspect-square', LEVELS[sampleLevel(d, h)])}
                  initial={false}
                  animate={reduce || inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
                  transition={{ duration: 0.4, ease: OUT_EASE, delay: reduce ? 0 : h * 0.025 + d * 0.03 }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Field completion for one template: the field people skip stands out. */
function FieldCompletionStage({ title, active }: { title: string; active: boolean }) {
  const seen = useSeen(active)
  const fields = [
    { name: 'Client name', rate: 98 },
    { name: 'Project scope', rate: 91 },
    { name: 'Start date', rate: 87 },
    { name: 'Budget breakdown', rate: 46 },
    { name: 'Sign-off owner', rate: 79 },
  ]
  return (
    <Stage accent="purple">
      <WindowBar app="word" title={title} meta="Field completion" />
      <ul className="flex flex-1 flex-col justify-center gap-[1.1em] px-[1.4em] py-[1em]">
        {fields.map((f, i) => {
          const low = f.rate < 60
          return (
            <li key={f.name}>
              <div className="flex items-baseline justify-between">
                <span className={low ? 'font-semibold' : fragMuted}>{f.name}</span>
                <span className="tabular-nums font-semibold">{f.rate}%</span>
              </div>
              <div className={cn('mt-[0.35em] h-[0.7em] border', fragHair)}>
                <motion.span
                  className="block h-full origin-left"
                  style={{ width: `${f.rate}%`, backgroundColor: low ? '#ff00f2' : 'var(--color-landing-accent)' }}
                  initial={false}
                  animate={{ scaleX: seen ? 1 : 0 }}
                  transition={{ duration: 0.7, ease: OUT_EASE, delay: seen ? 0.15 + i * 0.08 : 0 }}
                />
              </div>
            </li>
          )
        })}
      </ul>
    </Stage>
  )
}

/**
 * Analytics ("heatmap"): a white hero whose picture is the hour-by-day usage
 * heatmap, the stats beneath it; pinned use cases with analytics fragments
 * (trend, field completion, activity log); capabilities as a grid; impact as
 * rotated cards; FAQ; dark CTA.
 */
export function AnalyticsLayout({ feature }: LayoutProps) {
  return (
    <>
      {/* 1. Heatmap hero */}
      <section aria-labelledby="analytics-title" className="overflow-x-clip bg-[var(--color-landing-gray-50)] pb-16 pt-16 dark:bg-[#12121a] md:pb-24 md:pt-24">
        <div className="landing-container">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-end lg:gap-16">
            <div>
              <HeroReveal delay={0}>
                <p className="landing-text-display-lg">
                  <span className="inline-block -rotate-1 bg-[#ff00f2] px-[0.3em] text-[#0a0a0a]">{feature.shortLabel}.</span>
                </p>
              </HeroReveal>
              <HeroReveal delay={0.1}>
                <h1 id="analytics-title" className={cn('landing-text-hero-sm mt-6', ink)}>
                  <span className="block">{feature.heroLines[0]}</span>{' '}
                  <span className="block">{feature.heroLines[1]}</span>
                </h1>
              </HeroReveal>
            </div>
            <HeroReveal delay={0.2}>
              <p className="landing-text-body-lg max-w-[48ch] text-pretty border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {feature.subheadline}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <TrialButton />
                <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep">
                  Book a demo
                </Link>
              </div>
            </HeroReveal>
          </div>

          <HeroReveal delay={0.3} className="mt-12 md:mt-16">
            <Heatmap />
          </HeroReveal>

          <dl className="mt-12 grid grid-cols-1 gap-y-8 sm:grid-cols-3 sm:gap-x-8 md:mt-16">
            {feature.heroStats.map((stat) => (
              <div key={stat.label} className={cn('flex flex-col-reverse justify-end border-t-2 border-[#0a0a0a] pt-5 dark:border-white')}>
                <dt className={cn('mt-2 text-base', muted)}>{stat.label}</dt>
                <dd className={cn('font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,5vw,4.5rem)] uppercase leading-[1.25] tracking-[-0.02em]', ink)}>
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* 2. Use cases, pinned, with analytics fragments */}
      <section aria-labelledby="analytics-usecases-title" className="landing-section">
        <div className="landing-container mb-12 lg:mb-0">
          <TextReveal as="h2" className={cn('landing-text-display-xl', ink)}>
            <span id="analytics-usecases-title">How teams use {feature.shortLabel}</span>
          </TextReveal>
        </div>
        <StickyFeatures
          accent="pink"
          steps={feature.useCases.map((useCase) => ({
            title: useCase.title,
            description: useCase.description,
            extra: (
              <span className="inline-block bg-[var(--color-landing-primary)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
                {useCase.industry}
              </span>
            ),
            visual: (active: boolean) =>
              useCase.fragment === 'form' ? (
                <FieldCompletionStage title={useCase.title} active={active} />
              ) : (
                <UiFragment kind={useCase.fragment} title={useCase.title} industry={useCase.industry} active={active} />
              ),
          }))}
        />
      </section>

      {/* 3. Value proposition + capabilities */}
      <section aria-labelledby="analytics-value-title" className="landing-section pt-0">
        <div className="landing-container">
          <div className="mb-12 grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20 md:mb-16">
            <TextReveal as="h2" className={cn('landing-text-display-xl', ink)}>
              <span id="analytics-value-title">{feature.valueProposition.title}</span>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {feature.valueProposition.description}
              </p>
            </ScrollReveal>
          </div>
          <CapabilityGrid capabilities={feature.capabilities} />
        </div>
      </section>

      {/* 4. Impact as rotated cards */}
      <section aria-labelledby="analytics-impact-title" className="landing-section overflow-x-clip bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <TextReveal as="h2" className={cn('landing-text-display-xl', ink)}>
            <span id="analytics-impact-title">
              Business <span className="landing-highlight">impact.</span>
            </span>
          </TextReveal>
          <RotatedProcessCards
            className={cn('mt-16 lg:mt-10', hairline)}
            cards={feature.benefits.map((b) => ({ title: b.title, description: b.description, outcomes: b.stat ? [b.stat] : undefined }))}
          />
        </div>
      </section>

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="dark" />
    </>
  )
}
